import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { api } from './client'

export type TargetingLabel = 'Auto' | 'KW_Exact' | 'KW_Phrase' | 'KW_Broad' | 'KW_Mixed' | 'PAT' | 'Mixed'

export type PreviewBlocker =
  | 'UNSUPPORTED_SCOPE'
  | 'NO_PRODUCTS'
  | 'MAPPING_INCOMPLETE'
  | 'MAPPING_CONFLICT'
  | 'MULTIPLE_FAMILIES'
  | 'UNKNOWN_PPC_CODE'
  | 'MAPPING_SOURCE_UNKNOWN'
  | 'TARGETING_UNKNOWN'
  | 'NAME_COLLISION'
  | 'INVALID_NAME'

export type PreviewWarning =
  | 'IMPORTED_MAPPING_REQUIRES_REVIEW'
  | 'INFERRED_MAPPING_REQUIRES_REVIEW'
  | 'MIXED_TARGETING'

export interface CampaignNamingPreview {
  campaignId: number
  profileId: number
  amazonCampaignId: string
  currentName: string
  parentAsin: string | null
  ppcCode: string | null
  targeting: TargetingLabel | null
  proposedName: string | null
  childAsins: string[]
  inferredChildAsins: string[]
  mappingImportedAt: string | null
  blockers: PreviewBlocker[]
  warnings: PreviewWarning[]
}

export async function getCampaignNamingPreview(campaignId: number, profileId: number): Promise<CampaignNamingPreview> {
  const result = await api.get<{ preview: CampaignNamingPreview }>(
    `/campaigns/${campaignId}/naming-preview/?profileId=${profileId}`,
  )
  return result.preview
}

export function useCampaignNamingPreview(campaignId: number | null, profileId: number | null) {
  return useQuery({
    queryKey: ['naming', 'campaign-preview', profileId, campaignId],
    queryFn: () => getCampaignNamingPreview(campaignId!, profileId!),
    enabled: campaignId !== null && profileId !== null,
  })
}

// v29 Phase B — Naming-API Hooks
// Backend-Source-of-Truth fuer PPC-Codes, Name-Collision-Check und Format-Validierung.

export interface PpcCode {
  code: string
  categoryNumber: string
  category: string
  lager: string
  sku?: string
}

export function usePpcCodes() {
  return useQuery({
    queryKey: ['naming', 'ppc-codes'],
    queryFn: () => api.get<{ codes: PpcCode[] }>('/ppc-codes/'),
    staleTime: 1000 * 60 * 60, // 1 Stunde — Codes aendern sich selten
  })
}

export function useCheckCampaignName(name: string, profileId: number | null, debounceMs = 300) {
  const [debounced, setDebounced] = useState(name)

  useEffect(() => {
    const t = setTimeout(() => setDebounced(name), debounceMs)
    return () => clearTimeout(t)
  }, [name, debounceMs])

  return useQuery({
    queryKey: ['naming', 'check-name', debounced, profileId],
    queryFn: () =>
      api.get<{ exists: boolean; campaignId: number | null }>(
        `/campaigns/check-name/?name=${encodeURIComponent(debounced)}&profileId=${profileId}`,
      ),
    enabled: profileId !== null && debounced.length > 0,
    staleTime: 1000 * 30,
  })
}

export interface NameValidationResult {
  valid: boolean
  errors: string[]
}

export function useValidateCampaignName(name: string, debounceMs = 300) {
  const [debounced, setDebounced] = useState(name)

  useEffect(() => {
    const t = setTimeout(() => setDebounced(name), debounceMs)
    return () => clearTimeout(t)
  }, [name, debounceMs])

  return useQuery({
    queryKey: ['naming', 'validate-name', debounced],
    queryFn: () =>
      api.post<NameValidationResult>('/campaigns/validate-name/', { name: debounced }),
    enabled: debounced.length > 0,
    staleTime: 1000 * 30,
  })
}

// v36 — Browse-Tree-Picker
export interface BrowseNode {
  nodeId: string
  name: string
  parentId: string
  path: string
}

export function useBrowseTree(parentId?: string, query?: string) {
  return useQuery({
    queryKey: ['naming', 'browse-tree', parentId ?? 'ROOT', query ?? ''],
    queryFn: () => {
      const params = new URLSearchParams()
      if (parentId) params.set('parentId', parentId)
      if (query) params.set('q', query)
      const qs = params.toString()
      return api.get<{ nodes: BrowseNode[] }>(
        qs ? `/browse-tree/?${qs}` : '/browse-tree/',
      )
    },
    staleTime: 1000 * 60 * 60, // 1 h — Browse-Tree ist statisch
  })
}
